'use client';

import { Suspense, useLayoutEffect, useState } from 'react';

import { useRouter } from '../../utils/hooks/useRouter.ts';
import { Link, type LinkProps } from './link.tsx';

interface NavLinkProps extends LinkProps {
  // If true, the link will be considered active if the current pathname starts with the link's href.
  // This is useful for parent links that should be active when on a child page.
  isCurrentOnChildPages?: boolean;
}

/**
 * Resolves the current pathname and sets aria-current on the link
 * when it matches after hydration.
 */
const NavLinkWithPathname: React.FC<NavLinkProps> = ({
  href,
  isCurrentOnChildPages = false,
  ...linkProps
}) => {
  const { usePathname } = useRouter();
  // eslint-disable-next-line react-compiler/react-compiler -- usePathname is a framework-agnostic hook passed via RouterContext
  const pathname = usePathname();
  const [isCurrent, setIsCurrent] = useState(false);
  const hrefStr = href.toString();
  const isChildPath = pathname.startsWith(
    hrefStr.endsWith('/') ? hrefStr : `${hrefStr}/`,
  );

  useLayoutEffect(() => {
    // The server render cannot know the browser pathname, so the first client
    // render must match it before enabling active-route attributes. Using a
    // layout effect prevents a visible inactive-state flash before paint.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsCurrent(
      pathname === hrefStr || (isCurrentOnChildPages && isChildPath),
    );
  }, [href, isCurrentOnChildPages, pathname]);

  return (
    <Link
      href={href}
      {...linkProps}
      {...(isCurrent && { 'aria-current': 'page' as const })}
    />
  );
};

/**
 * A navigation-aware link that sets `aria-current="page"` when the link's
 * href matches the current pathname. Use this in navs (header, footer,
 * sidebar) where active state indication matters.
 *
 * For body content links, use `<Link>` directly — it's lighter and doesn't
 * subscribe to pathname changes.
 */
export const NavLink: React.FC<NavLinkProps> = ({
  isCurrentOnChildPages,
  ...linkProps
}) => {
  return (
    <Suspense fallback={<Link {...linkProps} />}>
      <NavLinkWithPathname
        isCurrentOnChildPages={isCurrentOnChildPages}
        {...linkProps}
      />
    </Suspense>
  );
};
